/**
 * TeamJiraViewTab - Jira Team View: epic priorities, active kanban by assignee, and backlog
 */
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  SearchOutlined as SearchOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  AddOutlined as AddOutlinedIcon,
  Bolt as BoltIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { teamApi } from '@/services/api';

// =============================================================================
// Types for API responses
// =============================================================================

interface EpicChild {
  key: string;
  summary: string;
  status: string;
  assignee?: string | null;
  url?: string;
}

interface JiraEpic {
  key: string;
  summary: string;
  status: string;
  priority?: string | null;
  assignee?: string | null;
  duedate?: string | null;
  startdate?: string | null;
  url?: string;
  rank?: number;
  children: EpicChild[];
}

interface JiraEpicsResponse {
  items: JiraEpic[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ActiveTicket {
  key: string;
  summary: string;
  status: string;
  priority?: string | null;
  assignee?: string | null;
  url?: string;
  epicKey?: string | null;
}

interface BacklogTicket {
  key: string;
  summary: string;
  status: string;
  priority?: string | null;
  assignee?: string | null;
  url?: string;
  epicKey?: string | null;
  epicSummary?: string | null;
}

// =============================================================================
// Helper utilities
// =============================================================================

const PRIORITY_COLORS: Record<string, string> = {
  Highest: '#FF5630',
  High: '#FF7452',
  Medium: '#FFAB00',
  Low: '#2684FF',
  Lowest: '#B3BAC5',
};

const AVATAR_COLORS = ['#6554C0', '#0065FF', '#FF5630', '#36B37E', '#FF991F'];

function getAvatarColor(name: string): string {
  return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function daysUntil(duedateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(duedateStr);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// Sub-components
// =============================================================================

function StatusChip({ status }: { status: string }) {
  const theme = useTheme();

  const colorMap: Record<string, { bg: string; text: string }> = {
    'In Progress': {
      bg: theme.palette.info.main,
      text: theme.palette.info.contrastText,
    },
    'In Review': {
      bg: theme.palette.warning.main,
      text: theme.palette.warning.contrastText,
    },
    Done: {
      bg: theme.palette.success.main,
      text: theme.palette.success.contrastText,
    },
    'To Do': {
      bg: theme.palette.action.selected,
      text: theme.palette.text.primary,
    },
  };

  const colors = colorMap[status] ?? {
    bg: theme.palette.action.selected,
    text: theme.palette.text.secondary,
  };

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        fontWeight: 500,
        fontSize: '0.7rem',
        whiteSpace: 'nowrap',
      }}
    />
  );
}

function DaysRemainingChip({ duedate }: { duedate: string }) {
  const theme = useTheme();
  const days = daysUntil(duedate);

  let bg: string;
  let text: string;
  let label: string;

  if (days < 0) {
    bg = theme.palette.error.main;
    text = theme.palette.error.contrastText;
    label = `Overdue by ${Math.abs(days)}d`;
  } else if (days <= 14) {
    bg = theme.palette.warning.main;
    text = theme.palette.warning.contrastText;
    label = `${days}d left`;
  } else {
    bg = theme.palette.success.main;
    text = theme.palette.success.contrastText;
    label = `${days}d left`;
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bg,
        color: text,
        fontWeight: 600,
        fontSize: '0.7rem',
        whiteSpace: 'nowrap',
      }}
    />
  );
}

function AssigneeAvatar({ name }: { name: string }) {
  const color = getAvatarColor(name);
  const initials = getInitials(name);
  return (
    <Tooltip title={name}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: color,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.65rem',
          fontWeight: 700,
          flexShrink: 0,
          cursor: 'default',
        }}
      >
        {initials}
      </Box>
    </Tooltip>
  );
}

function PriorityDot({ priority }: { priority?: string | null }) {
  if (!priority) return null;
  const color = PRIORITY_COLORS[priority] ?? '#B3BAC5';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {priority}
      </Typography>
    </Box>
  );
}

// =============================================================================
// Section: Epic Priority List (drag-and-drop sortable, table-based)
// =============================================================================

interface SortableEpicTableRowProps {
  epic: JiraEpic;
  rank: number;
  expanded: boolean;
  onToggleExpand: (key: string) => void;
}

function SortableEpicTableRow({ epic, rank, expanded, onToggleExpand }: SortableEpicTableRowProps) {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: epic.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isOverdue = epic.duedate ? daysUntil(epic.duedate) < 0 : false;

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        sx={{
          backgroundColor: isDragging ? theme.palette.action.selected : 'inherit',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}
      >
        {/* Drag handle */}
        <TableCell sx={{ width: 32, px: 0.5, py: 0.75 }}>
          <Tooltip title="Drag to reorder">
            <Box
              {...attributes}
              {...listeners}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'grab',
                color: 'text.disabled',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          </Tooltip>
        </TableCell>

        {/* Rank */}
        <TableCell sx={{ width: 32, px: 0.5, py: 0.75, color: 'text.secondary', fontSize: '0.75rem' }}>
          {rank}
        </TableCell>

        {/* Epic: expand toggle + summary */}
        <TableCell sx={{ py: 0.75, minWidth: 200 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Epic">
              <BoltIcon sx={{ fontSize: 16, color: '#6554C0', flexShrink: 0 }} />
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => onToggleExpand(epic.key)}
              sx={{ p: 0.25, flexShrink: 0 }}
              aria-label={expanded ? 'Collapse children' : 'Expand children'}
              disabled={epic.children.length === 0}
            >
              {expanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon
                  fontSize="small"
                  sx={{ color: epic.children.length === 0 ? 'text.disabled' : 'text.secondary' }}
                />
              )}
            </IconButton>
            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
              {epic.summary}
            </Typography>
          </Box>
        </TableCell>

        {/* Key */}
        <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
          <Typography
            variant="caption"
            component="a"
            href={epic.url ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {epic.key}
          </Typography>
        </TableCell>

        {/* Status */}
        <TableCell sx={{ py: 0.75 }}>
          <StatusChip status={epic.status} />
        </TableCell>

        {/* Priority */}
        <TableCell sx={{ py: 0.75 }}>
          <PriorityDot priority={epic.priority} />
        </TableCell>

        {/* Start Date */}
        <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'text.secondary' }}>
          {epic.startdate ? formatDate(epic.startdate) : '—'}
        </TableCell>

        {/* Due Date */}
        <TableCell
          sx={{
            py: 0.75,
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            color: isOverdue ? 'error.main' : 'text.secondary',
            fontWeight: isOverdue ? 600 : 400,
          }}
        >
          {epic.duedate ? formatDate(epic.duedate) : '—'}
        </TableCell>

        {/* Days Left */}
        <TableCell sx={{ py: 0.75 }}>
          {epic.duedate ? <DaysRemainingChip duedate={epic.duedate} /> : '—'}
        </TableCell>

        {/* Assignee */}
        <TableCell sx={{ py: 0.75 }}>
          {epic.assignee ? <AssigneeAvatar name={epic.assignee} /> : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Child rows (expanded inline) */}
      {expanded &&
        epic.children.map((child) => {
          const done = child.status === 'Resolved' || child.status === 'Closed';
          return (
          <TableRow
            key={child.key}
            sx={{
              backgroundColor: done
                ? (theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.10)')
                : theme.palette.action.hover,
              '&:hover': {
                backgroundColor: done
                  ? (theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.25)' : 'rgba(76,175,80,0.18)')
                  : theme.palette.action.selected,
              },
            }}
          >
            <TableCell colSpan={10} sx={{ py: 0.5, pl: '44px', pr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  component="a"
                  href={child.url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {child.key}
                </Typography>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 80 }}>
                  {child.summary}
                </Typography>
                <StatusChip status={child.status} />
                {child.assignee ? (
                  <AssigneeAvatar name={child.assignee} />
                ) : (
                  <Typography variant="caption" color="text.disabled">—</Typography>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ); })}
    </>
  );
}

function epicOrderKey(teamId: string) {
  return `jira-epic-order-${teamId}`;
}

function applyStoredOrder(epics: JiraEpic[], teamId: string): JiraEpic[] {
  const stored = localStorage.getItem(epicOrderKey(teamId));
  if (!stored) return [...epics].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  try {
    const order: string[] = JSON.parse(stored);
    const byKey = Object.fromEntries(epics.map((e) => [e.key, e]));
    const ordered = order.filter((k) => byKey[k]).map((k) => byKey[k]);
    const remaining = epics.filter((e) => !order.includes(e.key));
    return [...ordered, ...remaining];
  } catch {
    return [...epics].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  }
}

function EpicPriorityList({
  epics,
  teamId,
  page,
  pages,
  total,
  onPageChange,
}: {
  epics: JiraEpic[];
  teamId: string;
  page: number;
  pages: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const [ordered, setOrdered] = useState<JiraEpic[]>(() => applyStoredOrder(epics, teamId));
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());

  useEffect(() => {
    setOrdered(applyStoredOrder(epics, teamId));
  }, [epics, teamId]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrdered((prev) => {
      const oldIdx = prev.findIndex((e) => e.key === active.id);
      const newIdx = prev.findIndex((e) => e.key === over.id);
      const next = arrayMove(prev, oldIdx, newIdx);
      localStorage.setItem(epicOrderKey(teamId), JSON.stringify(next.map((e) => e.key)));
      return next;
    });
  }

  function handleToggleExpand(key: string) {
    setExpandedEpics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <Box>
      {/* Section heading */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          ⚡ Epic Priority List
        </Typography>
        <Chip
          label={`${total} total`}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          Drag ≡ to reorder · ▶ to expand child tickets
        </Typography>
      </Box>

      {/* Info box */}
      <Box
        sx={{
          backgroundColor: '#FFF9C4',
          border: '1px solid #F9A825',
          borderRadius: 1,
          px: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Typography variant="caption" sx={{ color: '#5D4037', lineHeight: 1.5 }}>
          Reordering here updates the Jira Rank field. The Priority field (Highest → Lowest) is an
          independent urgency signal — edit it directly on the epic.
        </Typography>
      </Box>

      {ordered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No epics found.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
            <Table size="small" sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ width: 32, px: 0.5, py: 0.75 }} />
                  <TableCell sx={{ width: 32, px: 0.5, py: 0.75, fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem', minWidth: 200 }}>Epic</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Key</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem' }}>Priority</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Target Start</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Target End</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Days Left</TableCell>
                  <TableCell sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem' }}>Assignee</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={ordered.map((e) => e.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {ordered.map((epic, idx) => (
                      <SortableEpicTableRow
                        key={epic.key}
                        epic={epic}
                        rank={idx + 1}
                        expanded={expandedEpics.has(epic.key)}
                        onToggleExpand={handleToggleExpand}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Days Left:
            </Typography>
            <Chip
              label=">14d"
              size="small"
              sx={{ backgroundColor: 'success.main', color: 'success.contrastText', fontSize: '0.65rem', fontWeight: 600, height: 20 }}
            />
            <Chip
              label="≤14d"
              size="small"
              sx={{ backgroundColor: 'warning.main', color: 'warning.contrastText', fontSize: '0.65rem', fontWeight: 600, height: 20 }}
            />
            <Chip
              label="Overdue"
              size="small"
              sx={{ backgroundColor: 'error.main', color: 'error.contrastText', fontSize: '0.65rem', fontWeight: 600, height: 20 }}
            />
          </Box>

          {pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
              <Pagination
                count={pages}
                page={page}
                onChange={(_e, value) => onPageChange(value)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// =============================================================================
// Section: Active — In Progress by Assignee
// =============================================================================

function ActiveWorkByAssignee({ tickets }: { tickets: ActiveTicket[] }) {
  const theme = useTheme();

  const grouped = tickets.reduce<Record<string, ActiveTicket[]>>((acc, ticket) => {
    const assignee = ticket.assignee ?? 'Unassigned';
    if (!acc[assignee]) acc[assignee] = [];
    acc[assignee].push(ticket);
    return acc;
  }, {});

  const assignees = Object.keys(grouped).sort();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          🏃 Active — In Progress by Assignee
        </Typography>
        <Chip
          label={`${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
      </Box>

      {assignees.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active tickets found.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 2,
          }}
        >
          {assignees.map((assignee) => (
            <Card variant="outlined" key={assignee} sx={{ display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                avatar={<AssigneeAvatar name={assignee} />}
                title={
                  <Typography variant="subtitle2" fontWeight={700}>
                    {assignee}
                  </Typography>
                }
                subheader={
                  <Typography variant="caption" color="text.secondary">
                    {grouped[assignee].length} ticket{grouped[assignee].length !== 1 ? 's' : ''}
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, flex: 1 }}>
                {grouped[assignee].length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No active tickets
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {grouped[assignee].map((ticket) => (
                      <Box
                        key={ticket.key}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          p: 1,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.default,
                        }}
                      >
                        {/* Top row: key + status */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="a"
                            href={ticket.url ?? '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                              fontWeight: 600,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {ticket.key}
                          </Typography>
                          <StatusChip status={ticket.status} />
                        </Box>

                        {/* Summary */}
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                          {ticket.summary}
                        </Typography>

                        {/* Meta row: epic tag + priority */}
                        {(ticket.epicKey || ticket.priority) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {ticket.epicKey && (
                              <Chip
                                label={ticket.epicKey}
                                size="small"
                                sx={{
                                  backgroundColor: '#eae6ff',
                                  color: '#403294',
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                  height: 20,
                                }}
                              />
                            )}
                            <PriorityDot priority={ticket.priority} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

// =============================================================================
// Section: Backlog grouped by epic
// =============================================================================

interface SortableBacklogRowProps {
  ticket: BacklogTicket;
  dragDisabled: boolean;
}

function SortableBacklogRow({ ticket, dragDisabled }: SortableBacklogRowProps) {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.key,
    disabled: dragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    backgroundColor: isDragging ? theme.palette.action.selected : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
      {/* Drag handle */}
      <TableCell sx={{ py: 0.75, width: 28, px: 0.5 }}>
        {!dragDisabled && (
          <Tooltip title="Drag to reorder">
            <Box
              {...attributes}
              {...listeners}
              sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.disabled' }}
            >
              <DragIndicatorIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}
      </TableCell>

      {/* Key */}
      <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
        <Typography
          variant="caption"
          component="a"
          href={ticket.url ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: theme.palette.primary.main,
            textDecoration: 'none',
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {ticket.key}
        </Typography>
      </TableCell>

      {/* Summary */}
      <TableCell sx={{ py: 0.75 }}>
        <Typography variant="caption">{ticket.summary}</Typography>
      </TableCell>

      {/* Epic pill + title */}
      <TableCell sx={{ py: 0.75 }}>
        {ticket.epicKey ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={ticket.epicKey}
              size="small"
              sx={{
                backgroundColor: '#eae6ff',
                color: '#403294',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
                flexShrink: 0,
              }}
            />
            {ticket.epicSummary && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}
                title={ticket.epicSummary}
              >
                {ticket.epicSummary}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </TableCell>

      {/* Status */}
      <TableCell sx={{ py: 0.75 }}>
        <StatusChip status={ticket.status} />
      </TableCell>

      {/* Priority */}
      <TableCell sx={{ py: 0.75 }}>
        <PriorityDot priority={ticket.priority} />
      </TableCell>

      {/* Assignee */}
      <TableCell sx={{ py: 0.75 }}>
        {ticket.assignee ? (
          <AssigneeAvatar name={ticket.assignee} />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            Unassigned
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

function BacklogSection({ tickets }: { tickets: BacklogTicket[] }) {
  const [sortField, setSortField] = useState<keyof BacklogTicket | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Build initial grouped state from tickets prop
  function buildGrouped(ts: BacklogTicket[]): Record<string, BacklogTicket[]> {
    return ts.reduce<Record<string, BacklogTicket[]>>((acc, ticket) => {
      const key = ticket.epicKey ?? '__no_epic__';
      if (!acc[key]) acc[key] = [];
      acc[key].push(ticket);
      return acc;
    }, {});
  }

  const [groupedState, setGroupedState] = useState<Record<string, BacklogTicket[]>>(() =>
    buildGrouped(tickets)
  );

  useEffect(() => {
    setGroupedState(buildGrouped(tickets));
  }, [tickets]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const dragDisabled = sortField !== null;

  function handleSort(field: keyof BacklogTicket) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function displayTickets(group: BacklogTicket[]): BacklogTicket[] {
    if (!sortField) return group;
    return [...group].sort((a, b) => {
      const av = String(a[sortField] ?? '');
      const bv = String(b[sortField] ?? '');
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setGroupedState((prev) => {
      const next = { ...prev };
      for (const epicKey of Object.keys(next)) {
        const group = next[epicKey];
        const oldIdx = group.findIndex((t) => t.key === String(active.id));
        if (oldIdx === -1) continue;
        const newIdx = group.findIndex((t) => t.key === String(over.id));
        if (newIdx === -1) continue;
        next[epicKey] = arrayMove(group, oldIdx, newIdx);
        break;
      }
      return next;
    });
  }

  // epicSummary lookup from original tickets
  const epicSummaryMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    tickets.forEach((t) => {
      if (t.epicKey) map[t.epicKey] = t.epicSummary ?? null;
    });
    return map;
  }, [tickets]);

  const epicKeys = Object.keys(groupedState).sort((a, b) => {
    if (a === '__no_epic__') return 1;
    if (b === '__no_epic__') return -1;
    return a.localeCompare(b);
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          📋 Backlog
        </Typography>
        <Chip
          label={`${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          {dragDisabled ? 'Sorted by column · clear sort to drag-reorder' : 'Drag ≡ to reorder within epic group · click header to sort'}
        </Typography>
      </Box>

      {epicKeys.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No backlog tickets found.
        </Typography>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  {/* Drag handle column */}
                  <TableCell sx={{ width: 28, px: 0.5, py: 0.75 }} />
                  {(
                    [
                      { field: 'key' as const, label: 'Key', sx: { whiteSpace: 'nowrap' } },
                      { field: 'summary' as const, label: 'Summary', sx: { minWidth: 200 } },
                      { field: 'epicKey' as const, label: 'Epic', sx: {} },
                      { field: 'status' as const, label: 'Status', sx: {} },
                      { field: 'priority' as const, label: 'Priority', sx: {} },
                      { field: 'assignee' as const, label: 'Assignee', sx: {} },
                    ] as Array<{ field: keyof BacklogTicket; label: string; sx: object }>
                  ).map(({ field, label, sx }) => (
                    <TableCell
                      key={field}
                      sx={{ py: 0.75, fontWeight: 700, fontSize: '0.75rem', ...sx }}
                      sortDirection={sortField === field ? sortDir : false}
                    >
                      <TableSortLabel
                        active={sortField === field}
                        direction={sortField === field ? sortDir : 'asc'}
                        onClick={() => handleSort(field)}
                      >
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {epicKeys.map((epicKey) => {
                  const group = groupedState[epicKey];
                  const epicSummary = epicSummaryMap[epicKey] ?? null;
                  const headerLabel =
                    epicKey === '__no_epic__'
                      ? 'No Epic'
                      : epicSummary
                      ? `⚡ ${epicSummary} (${epicKey})`
                      : `⚡ ${epicKey}`;

                  return (
                    <Fragment key={epicKey}>
                      {/* Group header row */}
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            py: 0.75,
                            px: 2,
                            backgroundColor: 'action.hover',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                            }}
                          >
                            {headerLabel}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Ticket rows with DnD */}
                      <SortableContext
                        items={group.map((t) => t.key)}
                        strategy={verticalListSortingStrategy}
                      >
                        {displayTickets(group).map((ticket) => (
                          <SortableBacklogRow
                            key={ticket.key}
                            ticket={ticket}
                            dragDisabled={dragDisabled}
                          />
                        ))}
                      </SortableContext>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DndContext>
      )}
    </Box>
  );
}

// =============================================================================
// Loading skeleton
// =============================================================================

function LoadingSkeleton() {
  return (
    <Stack spacing={3}>
      <Skeleton variant="rectangular" height={40} />
      <Skeleton variant="rectangular" height={200} />
      <Skeleton variant="rectangular" height={40} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Skeleton variant="rectangular" height={160} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Skeleton variant="rectangular" height={160} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Skeleton variant="rectangular" height={160} />
        </Grid>
      </Grid>
      <Skeleton variant="rectangular" height={40} />
      <Skeleton variant="rectangular" height={200} />
    </Stack>
  );
}

// =============================================================================
// Main component
// =============================================================================

export default function TeamJiraViewTab() {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchText, setSearchText] = useState('');
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
  const [epicsPage, setEpicsPage] = useState(1);

  const queryClient = useQueryClient();

  const {
    data: epicsResponse,
    isLoading: epicsLoading,
    isFetching: epicsFetching,
    isError: epicsError,
  } = useQuery<JiraEpicsResponse>({
    queryKey: ['jira-view-epics', teamId, epicsPage],
    queryFn: () => teamApi.getJiraViewEpics(teamId!, epicsPage).then((res) => res.data),
    enabled: !!teamId,
    retry: false,
  });

  const epicsItems = epicsResponse?.items ?? [];

  const {
    data: active,
    isLoading: activeLoading,
    isFetching: activeFetching,
    isError: activeError,
  } = useQuery<ActiveTicket[]>({
    queryKey: ['jira-view-active', teamId],
    queryFn: () => teamApi.getJiraViewActive(teamId!).then((res) => res.data),
    enabled: !!teamId,
    retry: false,
  });

  const {
    data: backlog,
    isLoading: backlogLoading,
    isFetching: backlogFetching,
    isError: backlogError,
  } = useQuery<BacklogTicket[]>({
    queryKey: ['jira-view-backlog', teamId],
    queryFn: () => teamApi.getJiraViewBacklog(teamId!).then((res) => res.data),
    enabled: !!teamId,
    retry: false,
  });

  const isLoading = epicsLoading || activeLoading || backlogLoading;
  const isFetching = epicsFetching || activeFetching || backlogFetching;

  const handleRefresh = () => {
    setEpicsPage(1);
    queryClient.invalidateQueries({ queryKey: ['jira-view-epics', teamId] });
    queryClient.invalidateQueries({ queryKey: ['jira-view-active', teamId] });
    queryClient.invalidateQueries({ queryKey: ['jira-view-backlog', teamId] });
  };

  const allMembers = useMemo(() => {
    const set = new Set<string>();
    (active ?? []).forEach((t) => { if (t.assignee) set.add(t.assignee); });
    (backlog ?? []).forEach((t) => { if (t.assignee) set.add(t.assignee); });
    return Array.from(set).sort();
  }, [active, backlog]);

  const projectKeys = useMemo(() => {
    const set = new Set<string>();
    const prefix = (key: string) => key.split('-')[0];
    (active ?? []).forEach((t) => set.add(prefix(t.key)));
    (backlog ?? []).forEach((t) => set.add(prefix(t.key)));
    return Array.from(set).sort();
  }, [active, backlog]);

  const lc = searchText.toLowerCase();

  const filteredActive = useMemo(() => {
    return (active ?? []).filter((t) => {
      const matchesMember = !selectedMember || t.assignee === selectedMember;
      const matchesSearch = !lc ||
        t.key.toLowerCase().includes(lc) ||
        (t.summary ?? '').toLowerCase().includes(lc);
      const matchesBoard = !selectedBoard || t.key.startsWith(selectedBoard + '-');
      return matchesMember && matchesSearch && matchesBoard;
    });
  }, [active, selectedMember, lc, selectedBoard]);

  const filteredBacklog = useMemo(() => {
    return (backlog ?? []).filter((t) => {
      const matchesMember = !selectedMember || t.assignee === selectedMember;
      const matchesSearch = !lc ||
        t.key.toLowerCase().includes(lc) ||
        (t.summary ?? '').toLowerCase().includes(lc);
      const matchesBoard = !selectedBoard || t.key.startsWith(selectedBoard + '-');
      return matchesMember && matchesSearch && matchesBoard;
    });
  }, [backlog, selectedMember, lc, selectedBoard]);

  // If all three queries returned errors (likely 404 = Jira not configured), show a placeholder
  const allFailed = epicsError && activeError && backlogError;
  const noData =
    !isLoading &&
    !allFailed &&
    epicsItems.length === 0 &&
    (active ?? []).length === 0 &&
    (backlog ?? []).length === 0;

  // Show skeleton until every query has settled (data or error), regardless of cache state.
  const allSettled =
    (epicsResponse !== undefined || epicsError) &&
    (active !== undefined || activeError) &&
    (backlog !== undefined || backlogError);

  if (!teamId || !allSettled) {
    return <LoadingSkeleton />;
  }

  if (allFailed) {
    return (
      <Alert severity="info">
        Jira is not configured for this team, or the Team View endpoint is not available. Configure
        Jira integration in Team Settings and ensure the backend supports the{' '}
        <code>/jira-view</code> API.
      </Alert>
    );
  }

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <Stack spacing={4}>
      {/* Page-level header + toolbar */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Team Board — Full View
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {today}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title={isFetching ? 'Refreshing…' : 'Refresh'}>
              <span>
                <IconButton size="small" onClick={handleRefresh} disabled={isFetching}>
                  {isFetching && !isLoading
                    ? <CircularProgress size={16} />
                    : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Column settings">
              <IconButton size="small"><SettingsOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton size="small"><FileDownloadOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Create ticket">
              <IconButton size="small"><AddOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        </Box>
        {isFetching && !isLoading && (
          <LinearProgress sx={{ borderRadius: 1, mt: 0.5 }} />
        )}
      </Box>

      {epicsError && <Alert severity="warning">Failed to load epics data.</Alert>}
      {activeError && <Alert severity="warning">Failed to load active work data.</Alert>}
      {backlogError && <Alert severity="warning">Failed to load backlog data.</Alert>}

      {noData && (
        <Alert severity="info">
          No Jira data found. Make sure your Jira project is configured and tickets are assigned.
        </Alert>
      )}

      {!epicsError && (
        <EpicPriorityList
          epics={epicsItems}
          teamId={teamId!}
          page={epicsResponse?.page ?? 1}
          pages={epicsResponse?.pages ?? 1}
          total={epicsResponse?.total ?? 0}
          onPageChange={setEpicsPage}
        />
      )}

      <Divider />

      {/* Filter bar — search + member + board chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Filter…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, maxWidth: 280 }}
        />

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label="All"
            size="small"
            variant={selectedMember === null ? 'filled' : 'outlined'}
            onClick={() => setSelectedMember(null)}
            sx={{ fontWeight: 600 }}
          />
          {allMembers.map((member) => (
            <Tooltip key={member} title={member}>
              <Chip
                label={getInitials(member)}
                size="small"
                variant={selectedMember === member ? 'filled' : 'outlined'}
                onClick={() => setSelectedMember(selectedMember === member ? null : member)}
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          ))}
        </Box>

        {projectKeys.length > 1 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label="All boards"
                size="small"
                variant={!selectedBoard ? 'filled' : 'outlined'}
                onClick={() => setSelectedBoard(null)}
                sx={{ fontWeight: 600 }}
              />
              {projectKeys.map((pk) => (
                <Chip
                  key={pk}
                  label={pk}
                  size="small"
                  variant={selectedBoard === pk ? 'filled' : 'outlined'}
                  onClick={() => setSelectedBoard(selectedBoard === pk ? null : pk)}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {!activeError && <ActiveWorkByAssignee tickets={filteredActive} />}

      {(!activeError || !backlogError) && <Divider />}

      {!backlogError && <BacklogSection tickets={filteredBacklog} />}
    </Stack>
  );
}
